import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  MessageSquare, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { verifyAdminToken, adminLogout } from '../../services/api';
import { toast } from 'sonner';

const AdminLayout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isValid = await verifyAdminToken();
    if (!isValid) {
      navigate('/admin/login');
    } else {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  };

  const handleLogout = async () => {
    await adminLogout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/blogs', label: 'Blog Posts', icon: FileText },
    { path: '/admin/inquiries', label: 'Inquiries', icon: MessageSquare },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--cream-bg)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--primary-teal)] border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--cream-bg)' }} data-testid="admin-layout">
      {/* Sidebar - Desktop */}
      <aside 
        className="hidden lg:flex lg:flex-col lg:w-64 bg-white shadow-lg"
        style={{ borderRight: '1px solid var(--gold-accent)' }}
      >
        <div className="p-6" style={{ backgroundColor: 'var(--primary-teal)' }}>
          <Link to="/" className="flex items-center gap-2">
            <img src="/triton_logo.png" alt="Triton" className="h-10 w-auto" />
            <span className="text-white font-bold text-lg">Admin</span>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-[var(--primary-teal)] text-white' 
                    : 'text-[var(--text-dark)] hover:bg-gray-100'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
                {isActive && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-lg">
            <div className="p-6 flex items-center justify-between" style={{ backgroundColor: 'var(--primary-teal)' }}>
              <Link to="/" className="flex items-center gap-2">
                <img src="/triton_logo.png" alt="Triton" className="h-8 w-auto" />
                <span className="text-white font-bold">Admin</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="text-white">
                <X size={24} />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-[var(--primary-teal)] text-white' 
                        : 'text-[var(--text-dark)] hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t">
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full justify-start gap-2 text-red-600"
              >
                <LogOut size={18} />
                Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white shadow-sm p-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <Menu size={24} style={{ color: 'var(--primary-teal)' }} />
          </button>
          <span className="font-bold" style={{ color: 'var(--primary-teal)' }}>Admin Panel</span>
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
